import React, { useEffect, useRef, useState } from "react";
import styles from "./GooglePayButton.module.css";

const GOOGLE_PAY_ENABLED =
  String(process.env.REACT_APP_GOOGLE_PAY_ENABLED || "").toLowerCase() ===
  "true";
const GOOGLE_PAY_ENVIRONMENT =
  String(process.env.REACT_APP_GOOGLE_PAY_ENVIRONMENT || "TEST").toUpperCase() ===
  "PRODUCTION"
    ? "PRODUCTION"
    : "TEST";
const GOOGLE_PAY_GATEWAY_MERCHANT_ID =
  GOOGLE_PAY_ENVIRONMENT === "TEST"
    ? "googletest"
    : String(
        process.env.REACT_APP_GOOGLE_PAY_GATEWAY_MERCHANT_ID || ""
      ).trim();
const GOOGLE_PAY_MERCHANT_NAME = String(
  process.env.REACT_APP_GOOGLE_PAY_MERCHANT_NAME || "MyZooShop"
).trim();

const waitForGooglePaySdk = () =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.google?.payments?.api) {
        window.clearInterval(timer);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= 10000) {
        window.clearInterval(timer);
        reject(new Error("Google Pay SDK не завантажився."));
      }
    }, 100);
  });

const GooglePayButton = ({
  disabled = false,
  preparePayment,
  submitPayment,
  onCompleted,
  onError,
  testTotalAmount = 0,
}) => {
  const containerRef = useRef(null);
  const processingRef = useRef(false);
  const disabledRef = useRef(disabled);
  const callbacksRef = useRef({});
  const [isProcessing, setIsProcessing] = useState(false);

  disabledRef.current = disabled;
  callbacksRef.current = {
    preparePayment,
    submitPayment,
    onCompleted,
    onError,
    testTotalAmount,
  };

  const isConfigured = Boolean(
    GOOGLE_PAY_ENABLED && GOOGLE_PAY_GATEWAY_MERCHANT_ID
  );

  useEffect(() => {
    if (!isConfigured || !containerRef.current) return undefined;

    let disposed = false;
    const container = containerRef.current;

    const initialize = async () => {
      try {
        await waitForGooglePaySdk();
        if (disposed) return;

        const paymentsClient =
          new window.google.payments.api.PaymentsClient({
            environment: GOOGLE_PAY_ENVIRONMENT,
          });

        const button = paymentsClient.createButton({
          buttonColor: "black",
          buttonType: "pay",
          buttonSizeMode: "fill",
          onClick: async () => {
            if (processingRef.current || disabledRef.current) return;

            processingRef.current = true;
            setIsProcessing(true);

            try {
              const prepared =
                GOOGLE_PAY_ENVIRONMENT === "TEST"
                  ? {
                      totalAmount: callbacksRef.current.testTotalAmount,
                    }
                  : await callbacksRef.current.preparePayment?.();

              if (
                GOOGLE_PAY_ENVIRONMENT === "PRODUCTION" &&
                (!prepared?.orderId || !prepared?.idempotencyKey)
              ) {
                throw new Error(
                  "Не вдалося підготувати замовлення для Google Pay."
                );
              }

              const paymentDataRequest = {
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods: [
                  {
                    type: "CARD",
                    parameters: {
                      allowedAuthMethods: [
                        "PAN_ONLY",
                        "CRYPTOGRAM_3DS",
                      ],
                      allowedCardNetworks: ["VISA", "MASTERCARD"],
                    },
                    tokenizationSpecification: {
                      type: "PAYMENT_GATEWAY",
                      parameters: {
                        gateway: "monobank",
                        gatewayMerchantId:
                          GOOGLE_PAY_GATEWAY_MERCHANT_ID,
                      },
                    },
                  },
                ],
                merchantInfo: {
                  merchantName: GOOGLE_PAY_MERCHANT_NAME,
                },
                transactionInfo: {
                  totalPriceStatus: "FINAL",
                  totalPrice: Number(prepared.totalAmount || 0).toFixed(2),
                  currencyCode: "UAH",
                },
              };

              const paymentData =
                await paymentsClient.loadPaymentData(paymentDataRequest);

              if (GOOGLE_PAY_ENVIRONMENT === "TEST") {
                throw new Error(
                  "Google Pay TEST успішно повернув тестовий token. Monobank не проводить test tokens; для реального платежу перемкніть інтеграцію на PRODUCTION після approval."
                );
              }

              const result =
                await callbacksRef.current.submitPayment?.({
                  orderId: prepared.orderId,
                  idempotencyKey: prepared.idempotencyKey,
                  paymentData,
                });

              callbacksRef.current.onCompleted?.(result, prepared);
            } catch (error) {
              if (error?.statusCode !== "CANCELED") {
                callbacksRef.current.onError?.(error);
              }
            } finally {
              processingRef.current = false;
              setIsProcessing(false);
            }
          },
        });

        container.replaceChildren(button);
      } catch {
        // Hosted checkout Monobank залишається доступним як fallback.
      }
    };

    initialize();

    return () => {
      disposed = true;
      container.replaceChildren();
    };
  }, [isConfigured]);

  if (!isConfigured) return null;

  return (
    <div
      id="container"
      className={`${styles.wrapper} ${
        disabled || isProcessing ? styles.disabled : ""
      }`}
      ref={containerRef}
      aria-busy={isProcessing}
    />
  );
};

export default GooglePayButton;
